export type ZipEntryInput = {
  path: string;
  data: string | Uint8Array;
};

export type ZipEntry = {
  path: string;
  data: Uint8Array;
};

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
const CRC32_TABLE = makeCrc32Table();
const UTF8_FLAG = 0x0800;
const STORE_METHOD = 0;

function makeCrc32Table(): Uint32Array {
  const table = new Uint32Array(256);
  for (let i = 0; i < table.length; i++) {
    let value = i;
    for (let bit = 0; bit < 8; bit++) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
}

function crc32(bytes: Uint8Array): number {
  let value = 0xffffffff;
  for (const byte of bytes) {
    value = CRC32_TABLE[(value ^ byte) & 0xff]! ^ (value >>> 8);
  }
  return (value ^ 0xffffffff) >>> 0;
}

function normalizeData(data: string | Uint8Array): Uint8Array {
  return typeof data === 'string' ? textEncoder.encode(data) : data;
}

function setU16(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
}

function setU32(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
  bytes[offset + 2] = (value >>> 16) & 0xff;
  bytes[offset + 3] = (value >>> 24) & 0xff;
}

function getU16(bytes: Uint8Array, offset: number): number {
  return bytes[offset]! | (bytes[offset + 1]! << 8);
}

function getU32(bytes: Uint8Array, offset: number): number {
  return (
    bytes[offset]! |
    (bytes[offset + 1]! << 8) |
    (bytes[offset + 2]! << 16) |
    (bytes[offset + 3]! << 24)
  ) >>> 0;
}

function encodePath(path: string): Uint8Array {
  if (path.length === 0 || path.includes('\\') || path.startsWith('/')) {
    throw new Error(`Invalid ZIP entry path: ${path}`);
  }
  return textEncoder.encode(path);
}

export function encodeZip(entries: readonly ZipEntryInput[]): Uint8Array {
  const normalized = entries.map((entry) => ({
    path: entry.path,
    pathBytes: encodePath(entry.path),
    data: normalizeData(entry.data),
  }));
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const entry of normalized) {
    const checksum = crc32(entry.data);
    const localHeader = new Uint8Array(30 + entry.pathBytes.length);
    setU32(localHeader, 0, 0x04034b50);
    setU16(localHeader, 4, 20);
    setU16(localHeader, 6, UTF8_FLAG);
    setU16(localHeader, 8, STORE_METHOD);
    setU32(localHeader, 14, checksum);
    setU32(localHeader, 18, entry.data.length);
    setU32(localHeader, 22, entry.data.length);
    setU16(localHeader, 26, entry.pathBytes.length);
    localHeader.set(entry.pathBytes, 30);
    localParts.push(localHeader, entry.data);

    const centralHeader = new Uint8Array(46 + entry.pathBytes.length);
    setU32(centralHeader, 0, 0x02014b50);
    setU16(centralHeader, 4, 20);
    setU16(centralHeader, 6, 20);
    setU16(centralHeader, 8, UTF8_FLAG);
    setU16(centralHeader, 10, STORE_METHOD);
    setU32(centralHeader, 16, checksum);
    setU32(centralHeader, 20, entry.data.length);
    setU32(centralHeader, 24, entry.data.length);
    setU16(centralHeader, 28, entry.pathBytes.length);
    setU32(centralHeader, 42, offset);
    centralHeader.set(entry.pathBytes, 46);
    centralParts.push(centralHeader);

    offset += localHeader.length + entry.data.length;
  }

  const centralOffset = offset;
  const centralSize = centralParts.reduce((total, part) => total + part.length, 0);
  const eocd = new Uint8Array(22);
  setU32(eocd, 0, 0x06054b50);
  setU16(eocd, 8, normalized.length);
  setU16(eocd, 10, normalized.length);
  setU32(eocd, 12, centralSize);
  setU32(eocd, 16, centralOffset);

  const totalSize = centralOffset + centralSize + eocd.length;
  const archive = new Uint8Array(totalSize);
  let cursor = 0;
  for (const part of [...localParts, ...centralParts, eocd]) {
    archive.set(part, cursor);
    cursor += part.length;
  }
  return archive;
}

function toUint8Array(input: ArrayBuffer | Uint8Array): Uint8Array {
  return input instanceof Uint8Array ? input : new Uint8Array(input);
}

function findEndOfCentralDirectory(bytes: Uint8Array): number {
  for (let offset = bytes.length - 22; offset >= 0; offset--) {
    if (getU32(bytes, offset) === 0x06054b50) return offset;
  }
  throw new Error('Invalid ZIP archive');
}

export function decodeZip(input: ArrayBuffer | Uint8Array): Map<string, Uint8Array> {
  const bytes = toUint8Array(input);
  const eocdOffset = findEndOfCentralDirectory(bytes);
  const entryCount = getU16(bytes, eocdOffset + 10);
  const centralOffset = getU32(bytes, eocdOffset + 16);
  const entries = new Map<string, Uint8Array>();
  let cursor = centralOffset;

  for (let index = 0; index < entryCount; index++) {
    if (getU32(bytes, cursor) !== 0x02014b50) {
      throw new Error('Invalid ZIP central directory');
    }
    const method = getU16(bytes, cursor + 10);
    if (method !== STORE_METHOD) {
      throw new Error('Unsupported ZIP compression method');
    }
    const compressedSize = getU32(bytes, cursor + 20);
    const uncompressedSize = getU32(bytes, cursor + 24);
    if (compressedSize !== uncompressedSize) {
      throw new Error('Unsupported ZIP entry size');
    }
    const nameLength = getU16(bytes, cursor + 28);
    const extraLength = getU16(bytes, cursor + 30);
    const commentLength = getU16(bytes, cursor + 32);
    const localOffset = getU32(bytes, cursor + 42);
    const path = textDecoder.decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));

    if (getU32(bytes, localOffset) !== 0x04034b50) {
      throw new Error('Invalid ZIP local header');
    }
    const localNameLength = getU16(bytes, localOffset + 26);
    const localExtraLength = getU16(bytes, localOffset + 28);
    const dataOffset = localOffset + 30 + localNameLength + localExtraLength;
    entries.set(path, bytes.slice(dataOffset, dataOffset + uncompressedSize));

    cursor += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

export function decodeZipText(entry: Uint8Array): string {
  return textDecoder.decode(entry);
}
